import { readString, writeString } from './storage.js';

const DEVICE_ID_KEY = 'deviceId';
const GUEST_NAME_KEY = 'guestName';

/**
 * An identity handed to this page by a host that already knows whose browser
 * this is — in practice, the iMessage extension, which reads the app's device
 * id out of the shared App Group and passes it in.
 *
 * Without it that web view is its own storage jar with its own fresh anonymous
 * id, so a night spent swiping inside a message thread would be filed against a
 * stranger, and a group's history would never compound. That is the same
 * identity problem invite-claiming solves for everyone else's phone, one layer
 * down and on your own.
 *
 * Held in memory and deliberately never written to storage. The extension
 * passes it on every load, so persisting it buys nothing — and a URL is
 * something anyone can write, so a hostile link is worth keeping to the length
 * of one page view rather than letting it permanently rename a browser.
 */
const handed = (() => {
  try {
    const params = new URLSearchParams(window.location.search);
    const id = params.get('me')?.trim();
    return {
      // Shape-checked, not trusted: this only ever attributes votes inside one
      // disposable session, but it should not be able to be anything at all.
      id: id && /^[\w-]{4,64}$/.test(id) ? id : null,
      // The name the app already has, so nobody types it twice. Same reasoning
      // and the same lifetime as the id above.
      name: params.get('name')?.trim().slice(0, 40) || null,
    };
  } catch {
    return { id: null, name: null };
  }
})();

/**
 * A stable per-browser identifier, used to attribute votes inside a group
 * session. Anonymous by design — there is no account system yet, so this is
 * what stands in for a user id.
 *
 * @returns {string}
 */
export function getDeviceId() {
  if (handed.id) return handed.id;

  let id = readString(DEVICE_ID_KEY);
  if (!id) {
    id = `u_${randomIdPart()}`;
    writeString(DEVICE_ID_KEY, id);
  }
  return id;
}

function randomIdPart() {
  const browserCrypto = globalThis.crypto;
  if (typeof browserCrypto?.randomUUID === 'function')
    return browserCrypto.randomUUID().slice(0, 8);

  const bytes = new Uint8Array(4);
  if (typeof browserCrypto?.getRandomValues === 'function') {
    browserCrypto.getRandomValues(bytes);
  } else {
    for (let index = 0; index < bytes.length; index += 1) {
      bytes[index] = Math.floor(Math.random() * 256);
    }
  }

  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
}

/** @returns {string} The guest's display name, or an empty string. */
export function getGuestName() {
  return handed.name ?? readString(GUEST_NAME_KEY, '') ?? '';
}

/**
 * The identity supplied by the installed Crave app to its Messages extension.
 * A normal browser never gets one, even if it happens to have a remembered
 * guest name, so callers can safely use this to skip the join prompt only for
 * someone arriving through their own installed app.
 *
 * @returns {{id: string, name: string} | null}
 */
export function getAppIdentity() {
  if (!handed.id || !handed.name) return null;
  return { id: handed.id, name: handed.name };
}

/**
 * Remembers a display name typed on the web invite screen.
 *
 * A name handed over by the native app remains per-page-view only; a name the
 * person typed into this browser is their explicit preference for future group
 * joins from the same browser.
 *
 * @param {string} name
 * @returns {string}
 */
export function setGuestName(name) {
  const trimmed = name.trim().slice(0, 40);
  if (trimmed) writeString(GUEST_NAME_KEY, trimmed);
  return trimmed;
}

/** Two-letter monogram for avatars. Falls back to `?`. */
export function getInitials(name) {
  const letters = (name ?? '')
    .trim()
    .split(/\s+/)
    .map((word) => word[0]?.toUpperCase() ?? '')
    .join('');
  return letters.slice(0, 2) || '?';
}
